/**
 * EIDEntryStep Component
 *
 * Step 1: Enter the Equipment ID (EID)
 * Allows user to enter the G5 device EID with format validation.
 */

import * as React from 'react';
import styled from 'styled-components';
import { Icon } from '../../../../shared/Icon';
import { useWizard } from '../../hooks/useWizard';
import { HelpFooter } from '../HelpFooter';
import { KEYS } from '../../../../../keys';

/**
 * EID format pattern: 3-4 digit numeric (000-2999)
 */
const EID_PATTERN = /^(0?[0-9]{3}|[12][0-9]{3})$/;

/**
 * EIDEntryStep component
 */
export const EIDEntryStep: React.FC = (): React.ReactElement => {
  const { state, setEid, validateEid } = useWizard();

  const [eidInput, setEidInput] = React.useState(state.eid || '');

  /**
   * Handle EID input change - only allow numeric input
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    // Only allow digits
    const value = e.target.value.replace(/[^0-9]/g, '');
    setEidInput(value);

    // Update state and validate format
    setEid(value);
    const isValid = EID_PATTERN.test(value);
    validateEid(isValid);
  };

  const isFormatValid = EID_PATTERN.test(eidInput);

  return (
    <StepContainer data-testid={KEYS.WIZARD_EID_STEP}>
      {/* Combined Overview and Instructions Card */}
      <InstructionCard>
        <OverviewText>
          <p>
            An update to the Force Plate firmware is required for compatibility
            with the iOS version of the Capture app. This guide will help you
            complete the required update to your Force Plates.
          </p>
          <p>
            This update applies only to Generation 5 Force Plates. If your
            plates have a USB-C port on the front then they are Generation 5.
            The process takes approximately 5-10 minutes and requires a{' '}
            <strong>USB-C cable</strong> and your Force Plate&#39;s{' '}
            <strong>Equipment ID (EID).</strong>
          </p>
        </OverviewText>

        <Divider />

        <InstructionContent>
          <InstructionTitle data-testid={KEYS.WIZARD_EID_HEADING}>
            Find Your Equipment ID
          </InstructionTitle>
          <InstructionDescription>
            The EID is located on the sticker on the side of either of your
            force plates, and is a 3 or 4-digit number.
          </InstructionDescription>
          <InputRow>
            <InputWrapper>
              <EIDInput
                id='eid-input'
                data-testid={KEYS.WIZARD_EID_INPUT}
                type='text'
                placeholder='1234'
                value={eidInput}
                onChange={handleInputChange}
                maxLength={4}
                $isValid={isFormatValid}
                $hasError={eidInput.length > 0 && !isFormatValid}
              />
              {isFormatValid && (
                <InputSuccess>
                  <Icon name='check_circle' color='#66bb6a' size='small' />
                </InputSuccess>
              )}
            </InputWrapper>
            <InputHint>
              {!eidInput && 'Enter your EID'}
              {eidInput && !isFormatValid && 'Please enter a valid ID'}
              {isFormatValid && (
                <SuccessText data-testid={KEYS.WIZARD_EID_VALID}>
                  <Icon name='check' size='tiny' color='#66bb6a' /> Valid
                </SuccessText>
              )}
            </InputHint>
          </InputRow>
        </InstructionContent>
      </InstructionCard>

      <HelpFooter message='Having trouble finding your EID?' />
    </StepContainer>
  );
};

// Styled Components

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InstructionCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  background: var(--color-surface-1);
  border-radius: 10px;
`;

const OverviewText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.72);
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
  margin: 20px 0;
`;

const InstructionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InstructionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
`;

const InstructionDescription = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.72);
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const EIDInput = styled.input<{ $isValid?: boolean; $hasError?: boolean }>`
  width: 120px;
  padding: 10px 40px 10px 12px;
  font-size: 18px;
  font-family: monospace;
  letter-spacing: 2px;
  text-transform: uppercase;
  border: 2px solid
    ${(props: { $isValid?: boolean; $hasError?: boolean }): string => {
      if (props.$isValid) return '#66bb6a';
      if (props.$hasError) return 'var(--color-react-select-danger)';
      return 'var(--color-separator)';
    }};
  border-radius: 8px;
  background: var(--color-surface-2);
  color: var(--color-text);
  outline: none;
  transition: border-color 200ms ease;

  &:focus {
    border-color: var(--color-primary);
  }

  &::placeholder {
    color: var(--color-disabled-text);
    opacity: 0.5;
  }
`;

const InputSuccess = styled.div`
  position: absolute;
  right: 10px;
`;

const InputHint = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.72);
`;

const SuccessText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #66bb6a;
`;

export default EIDEntryStep;
